import Button from '@standardsdigital/ui-components/Button';
import ConfirmationDialog from '@standardsdigital/ui-components/ConfirmationDialog';
import Heading, { HeadingSize } from '@standardsdigital/ui-components/Heading';
import { useTranslation } from 'react-i18next';
import Spinner from '@/components/global/Spinner/Spinner';
import { useToast } from '@/util/hooks';
import {
  CustomSubscriptionConfigurationEntryType,
  type CreateCustomSubscriptionConfigurationEntryRequest,
  type UpdateCustomSubscriptionConfigurationEntryRequest,
  useCreateCustomSubscriptionConfigurationEntryMutation,
  useDeleteCustomSubscriptionConfigurationEntryMutation,
  useDeleteCustomSubscriptionConfigurationMutation,
  useGetCustomSubscriptionConfigurationQuery,
  useUpdateCustomSubscriptionConfigurationEntryMutation,
} from '@/redux/services/generate/configurations/customSubscriptionConfigurations';
import Configurator from './Configurator/Configurator';
import styles from './Configuration.module.css';

interface ConfigurationProps {
  selectedConfigurationId: string;
  onSelectConfiguration: (configurationId: string) => void;
}

function Configuration({
  selectedConfigurationId,
  onSelectConfiguration,
}: Readonly<ConfigurationProps>) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    data: selectedConfiguration,
    isLoading: isLoadingConfiguration,
    isError: isConfigurationLoadError,
  } = useGetCustomSubscriptionConfigurationQuery(
    { id: selectedConfigurationId },
    { skip: selectedConfigurationId === '' },
  );
  const [
    createCustomSubscriptionConfigurationEntry,
    { isLoading: isCreatingEntry },
  ] = useCreateCustomSubscriptionConfigurationEntryMutation();
  const [
    updateCustomSubscriptionConfigurationEntry,
    { isLoading: isUpdatingEntry },
  ] = useUpdateCustomSubscriptionConfigurationEntryMutation();
  const [
    deleteCustomSubscriptionConfigurationEntry,
    { isLoading: isDeletingEntry },
  ] = useDeleteCustomSubscriptionConfigurationEntryMutation();
  const [
    deleteCustomSubscriptionConfiguration,
    { isLoading: isDeletingConfiguration },
  ] = useDeleteCustomSubscriptionConfigurationMutation();

  const onCreateEntry = async (
    configurationId: string,
    entry: CreateCustomSubscriptionConfigurationEntryRequest,
  ) => {
    await createCustomSubscriptionConfigurationEntry({
      configurationId,
      createCustomSubscriptionConfigurationEntryRequest: entry,
    }).unwrap();
    toast.success(t('customSubscriptionConfiguration.entrySuccessfullyCreated'));
  };

  const onUpdateEntry = async (
    configurationId: string,
    entryId: string,
    entry: UpdateCustomSubscriptionConfigurationEntryRequest,
  ) => {
    await updateCustomSubscriptionConfigurationEntry({
      id: configurationId,
      entryId,
      updateCustomSubscriptionConfigurationEntryRequest: entry,
    }).unwrap();
    toast.success(t('customSubscriptionConfiguration.entrySuccessfullyUpdated'));
  };

  const onDeleteEntry = async (configurationId: string, entryId: string) => {
    await deleteCustomSubscriptionConfigurationEntry({
      id: configurationId,
      entryId,
    }).unwrap();
    toast.success(t('customSubscriptionConfiguration.entrySuccessfullyDeleted'));
  };

  const onDeleteConfiguration = async (configurationId: string) => {
    await deleteCustomSubscriptionConfiguration({ id: configurationId }).unwrap();
    onSelectConfiguration('');
    toast.success(t('customSubscriptionConfiguration.successfullyDeleted'));
  };

  if (selectedConfigurationId === '') {
    return (
      <section className={styles.panel}>
        <Heading size={HeadingSize.Sm} level={2}>
          {t('customSubscriptionConfiguration.configurationTitle')}
        </Heading>
        <p>{t('customSubscriptionConfiguration.noConfigurationSelected')}</p>
      </section>
    );
  }

  if (isLoadingConfiguration) {
    return <Spinner position="centerAbsolute" size="large" />;
  }

  if (isConfigurationLoadError || !selectedConfiguration) {
    return (
      <section className={styles.panel}>
        <Heading size={HeadingSize.Sm} level={2}>
          {t('customSubscriptionConfiguration.configurationTitle')}
        </Heading>
        <p>{t('customSubscriptionConfiguration.noConfigurationSelected')}</p>
      </section>
    );
  }

  const isMutatingEntry = isCreatingEntry || isUpdatingEntry || isDeletingEntry;

  return (
    <section>
      <div className={styles.headerRow}>
        <Heading size={HeadingSize.Lg} level={2}>
          {selectedConfiguration.nsbName}
        </Heading>
        <ConfirmationDialog
          title={t('customSubscriptionConfiguration.removeConfigurationTitle', {
            name: selectedConfiguration.nsbName,
          })}
          description={t(
            'customSubscriptionConfiguration.removeConfigurationDescription',
          )}
          closeButtonText={t('general.close')}
          cancelButtonText={t('general.cancel')}
          confirmButtonText={t(
            'customSubscriptionConfiguration.removeConfiguration',
          )}
          severity="danger"
          onConfirm={() => onDeleteConfiguration(selectedConfiguration.id)}
          modal={false}
        >
          <Button
            type="button"
            variant="secondary-danger"
            disabled={isDeletingConfiguration}
          >
            {t('customSubscriptionConfiguration.removeConfiguration')}
          </Button>
        </ConfirmationDialog>
      </div>
      <div className={styles.configurators}>
        <Configurator
          key={`${selectedConfiguration.id}-${CustomSubscriptionConfigurationEntryType.Size}`}
          title={t('customSubscriptionConfiguration.sizeEntriesTitle')}
          description={t('customSubscriptionConfiguration.sizeEntriesDescription')}
          addButtonText={t('customSubscriptionConfiguration.addCollectionSizeTier')}
          configurationId={selectedConfiguration.id}
          entryType={CustomSubscriptionConfigurationEntryType.Size}
          configurationEntries={selectedConfiguration.sizeEntries}
          onCreateEntry={onCreateEntry}
          onUpdateEntry={onUpdateEntry}
          onDeleteEntry={onDeleteEntry}
          isMutatingEntry={isMutatingEntry}
        />
        <Configurator
          key={`${selectedConfiguration.id}-${CustomSubscriptionConfigurationEntryType.UserCount}`}
          title={t('customSubscriptionConfiguration.userCountEntriesTitle')}
          description={t(
            'customSubscriptionConfiguration.userCountEntriesDescription',
          )}
          addButtonText={t('customSubscriptionConfiguration.addUserCountTier')}
          configurationId={selectedConfiguration.id}
          entryType={CustomSubscriptionConfigurationEntryType.UserCount}
          configurationEntries={selectedConfiguration.userCountEntries}
          onCreateEntry={onCreateEntry}
          onUpdateEntry={onUpdateEntry}
          onDeleteEntry={onDeleteEntry}
          isMutatingEntry={isMutatingEntry}
        />
      </div>
    </section>
  );
}

export default Configuration;
